import type { components } from '@heidi/schema/ml-scribe';

import type { Heidi2EvidenceTourStep } from './heidi-2-evidence-tour-atoms';

type ModalView = components['schemas']['ModalView'];

/**
 * Where a clinician got to on the Heidi II welcome, read from the
 * `T4_CUSTOM_AUDIENCE_HEIDI_2_OPT_IN_BANNER` record the onboarding host writes.
 *
 * The host records `engage` only for a finished tour, so any engage means
 * `completed`; a record without one means they chose "Explore later" or closed
 * the tour part-way, which the nudges treat the same way.
 */
export type Heidi2WelcomeOutcome = 'unseen' | 'skipped' | 'completed';

export const resolveHeidi2WelcomeOutcome = (
  view: ModalView | null | undefined
): Heidi2WelcomeOutcome => {
  if (!view) return 'unseen';
  return (view.engage_count ?? 0) >= 1 ? 'completed' : 'skipped';
};

export interface Heidi2ExploreLaterAudience {
  isHeidi2OnboardingEnabled: boolean;
  /** `billingInfo.heidi_version === 2`. */
  isHeidi2: boolean;
  welcomeOutcome: Heidi2WelcomeOutcome;
}

/** Everyone the nudges are for: on Heidi II, and past the welcome one way or another. */
const isInAudience = ({
  isHeidi2OnboardingEnabled,
  isHeidi2,
  welcomeOutcome,
}: Heidi2ExploreLaterAudience) =>
  isHeidi2OnboardingEnabled && isHeidi2 && welcomeOutcome !== 'unseen';

export interface Heidi2ExploreLaterBannerGate extends Heidi2ExploreLaterAudience {
  isBannerClosed: boolean;
  /** Includes paused sessions and Chronicle. */
  isClinicalRecordingActive: boolean;
  /** `user.is_managed`; unknown while loading counts as managed. */
  isUserManaged: boolean | null | undefined;
  /**
   * The strip waits until the Evidence tour has closed so it does not sit
   * under the spotlight. `true` after Got it / X on any step.
   */
  isEvidenceTourClosed: boolean;
}

/**
 * The top banner is for people who skipped the welcome. It waits until the
 * Evidence tour has closed, then stays until they close this strip or finish
 * the welcome tour.
 */
export const computeShouldShowHeidi2ExploreLaterBanner = (
  gate: Heidi2ExploreLaterBannerGate
): boolean =>
  isInAudience(gate) &&
  gate.welcomeOutcome === 'skipped' &&
  gate.isEvidenceTourClosed &&
  !gate.isBannerClosed &&
  !gate.isClinicalRecordingActive &&
  gate.isUserManaged === false;

export interface Heidi2EvidenceTourGate extends Heidi2ExploreLaterAudience {
  isEvidenceTourClosed: boolean;
}

/**
 * The Evidence tour is for everyone past the welcome until it closes — "Got it"
 * on its last spotlight, or the X on any of them. It always starts on the
 * rail's Home item, then Recents, then the Evidence-only + menu row.
 */
export const computeIsHeidi2EvidenceTourEligible = (
  gate: Heidi2EvidenceTourGate
): boolean => isInAudience(gate) && !gate.isEvidenceTourClosed;

/**
 * Whether the tour takes its queue slot here. Step 0 exists wherever the rail's
 * Home item is mounted (or while walking to Home after "Next"), so the Home dot
 * never shows without its spotlight; step 1 only on Home; step 2 only
 * while the Home composer's + menu is mounted, which it is not once a message
 * is sent or a chat opens. Off those, the tour must not hold the slot
 * invisibly and block other launch surfaces.
 */
export const computeShouldQueueHeidi2EvidenceTour = (
  gate: Heidi2EvidenceTourGate & {
    step: Heidi2EvidenceTourStep;
    isOnWorkHome: boolean;
    isRailAnchorMounted: boolean;
    isHeadingHome: boolean;
    isAddMenuMounted: boolean;
  }
): boolean => {
  if (!computeIsHeidi2EvidenceTourEligible(gate)) return false;
  if (gate.step === 0) return gate.isRailAnchorMounted || gate.isHeadingHome;
  if (gate.step === 2) return gate.isOnWorkHome && gate.isAddMenuMounted;
  return gate.isOnWorkHome;
};

/**
 * The Home dot goes with the rail spotlight: for everyone past the welcome,
 * only while the tour is still on step 0.
 */
export const computeShowsHeidi2HomeDot = (
  gate: Heidi2EvidenceTourGate & { step: Heidi2EvidenceTourStep }
): boolean => computeIsHeidi2EvidenceTourEligible(gate) && gate.step === 0;

/**
 * The checklist is for everyone past the welcome until it is closed. The host
 * keeps it folded while the Evidence tour is open, so it never covers a
 * spotlight.
 */
export const computeShouldShowHeidi2GetStartedChecklist = (
  gate: Heidi2ExploreLaterAudience & { isChecklistClosed: boolean }
): boolean => isInAudience(gate) && !gate.isChecklistClosed;
